import React, { createContext, useState, useContext, useEffect, useRef, useCallback} from 'react';
import api from '../api/client';
import useIdleLogout from '../hooks/useIdleLogout';
import IdleWarningModal from '../components/IdleWarningModal';
interface ElearningData {
  rating: number;
  totalPoints: number;
  streak: number;
  lastActivityDate?: string;
  completedTests?: string[];
  benefits?: any[];
  lastResetAt?: string;
}
interface User {
  id: string;
  name: string;
  email: string;
  role: 'user' | 'admin' | 'superadmin' | 'hr_manager' | 'recruiter' | 'employee' | 'sales_manager' | 'accountant' | 'kdp';
  blocked: boolean;
  companyId: string;
  companyName: string;
  company: string;
  paid: boolean;
  photo?: string;
  lastActive?: string;
  demoExpiresAt?: string;
  elearning?: ElearningData;

  companyModules?: any;
  isMainAdmin?: boolean;
  adminPermissions?: string[];
}
export interface LoginResult {
  ok: boolean;
  requires2FA?: boolean;
  tempToken?: string;
  requiresVerification?: boolean;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<LoginResult>;
  verify2FA: (tempToken: string, code: string) => Promise<boolean>;
  register: (name: string, email: string, password: string, company: string) => Promise<boolean>;
  logout: () => Promise<void>;
  updateUser: (updates: Partial<User>) => Promise<void>;
  refreshUser: () => Promise<void>;
}
const AuthContext = createContext<AuthContextType | undefined>(undefined);
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const heartbeatInterval = useRef<NodeJS.Timeout | null>(null);
  // Получение токена из любого хранилища (приоритет sessionStorage)
  const getStoredToken = (): string | null => {
    const sessionToken = sessionStorage.getItem('token');
    if (sessionToken) return sessionToken;
    return localStorage.getItem('token');
  };
  const sendHeartbeat = async () => {
    if (!user) return;
    try {
      await api.post('/users/heartbeat');
    } catch (err) {
      console.error('Heartbeat failed:', err);
    }
  };
  const clearHeartbeat = () => {
    if (heartbeatInterval.current) {
      clearInterval(heartbeatInterval.current);
      heartbeatInterval.current = null;
    }
  };
  const startHeartbeat = () => {
    clearHeartbeat();
    if (user) {
      sendHeartbeat();
      heartbeatInterval.current = setInterval(sendHeartbeat, 60000);
    }
  };
  const refreshUser = async () => {
    try {
      const res = await api.get('/auth/me');
        setUser(res.data.user);
    } catch (err) {
      console.error('Ошибка обновления данных пользователя:', err);
    }
  };
  useEffect(() => {
    const fetchUser = async () => {
      const token = getStoredToken();
      if (!token) {
        // Нет токена — сессия невалидна, чистим «висячий» user
        localStorage.removeItem('user');
        sessionStorage.removeItem('user');
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        // ★ Загружаем ВСЁ параллельно — потом одним setUser с полными данными
        const [userRes, permRes, compRes] = await Promise.all([
          api.get('/auth/me'),
          api.get('/users/permissions/me').catch((e) => { console.error('Permissions load error:', e); return { data: {} }; }),
          api.get('/users/my-company-modules').catch(() => ({ data: {} })),
        ]);

        // ★ ОДИН setUser с полным объектом — никакой race condition
        const fullUser = {
          ...userRes.data.user,
          isMainAdmin: userRes.data.user.isMainAdmin || userRes.data.user.email === 'admin@hrlama.ru',
          permissions: permRes.data || {},
          companyModules: compRes.data || {},
        };
        setUser(fullUser);
        localStorage.setItem('user', JSON.stringify(fullUser));
      } catch (err) {
        console.log('No active session');
        localStorage.removeItem('token');
        sessionStorage.removeItem('token');
        setUser(null);
      } finally {
        setLoading(false);
      }
    };
    fetchUser();
  }, []);
  useEffect(() => {
    startHeartbeat();
    return clearHeartbeat;
  }, [user]);
  // ─── Внутренний хелпер: сохранение токенов + загрузка прав ───
  const applySession = async (data: any): Promise<void> => {
    const { token, refreshToken, user } = data;
    localStorage.setItem('token', token);
    localStorage.setItem('refreshToken', refreshToken || '');
    sessionStorage.removeItem('token');

    // ★ Загружаем permissions + companyModules параллельно
    try {
      const [permRes, compRes] = await Promise.all([
        api.get('/users/permissions/me').catch((e) => { console.error('Permissions load error:', e); return { data: {} }; }),
        api.get('/users/my-company-modules').catch(() => ({ data: {} })),
      ]);

      // ★ ОДИН setUser с полным объектом
      setUser({
        ...user,
        permissions: permRes.data || {},
        companyModules: compRes.data || {},
      });
    } catch (e) {
      console.error('Ошибка загрузки permissions:', e);
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
    }
  };

  const login = async (email: string, password: string, rememberMe: boolean = false): Promise<LoginResult> => {
    try {
      const res = await api.post('/auth/login', { email, password });
      console.log('Ответ от /auth/login:', res.data);

      // 🔐 Если требуется 2FA — возвращаем без сохранения сессии
      if (res.data.requires2FA) {
        return {
          ok: false,
          requires2FA: true,
          tempToken: res.data.tempToken,
        };
      }

      const { token } = res.data;
      if (!token) {
        console.error('Сервер не вернул токен');
        return { ok: false };
      }

      await applySession(res.data);
      return { ok: true };
    } catch (err: any) {
      console.error('Login failed:', err);
      const data = err?.response?.data;
      if (data?.code === 'EMAIL_NOT_VERIFIED') {
        return {
          ok: false,
          requiresVerification: true,
          email: data.email,
        };
      }
      return { ok: false };
    }
  };

  // ─── Второй шаг логина: ввод 6-значного кода ───
  const verify2FA = async (tempToken: string, code: string): Promise<boolean> => {
    try {
      const res = await api.post('/auth/2fa/verify', { tempToken, code });
      if (!res.data?.token) {
        console.error('Сервер не вернул токен после 2FA');
        return false;
      }
      await applySession(res.data);
      return true;
    } catch (err) {
      console.error('2FA verification failed:', err);
      return false;
    }
  };
  const register = async (name: string, email: string, password: string, company: string): Promise<boolean> => {
    try {
      const res = await api.post('/auth/register', { name, email, password, company });
      const { token, refreshToken, user } = res.data;
      if (!token) throw new Error('Токен не получен от сервера');
      localStorage.setItem('token', token);
      localStorage.setItem('refreshToken', refreshToken || '');
      setUser(user);
      localStorage.setItem('user', JSON.stringify(user));
      return true;
    } catch (err) {
      console.error('Registration failed:', err);
      return false;
    }
  };
  const logout = async () => {
    console.log('[logout] Called');
    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');
      if (token) {
        console.log('[logout] POST /auth/logout');
        await api.post('/auth/logout');
      }
    } catch (err) {
      console.error('[logout] API error:', err);
    } finally {
      console.log('[logout] Cleanup + redirect');
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('user');
      localStorage.removeItem('hrlama_last_activity');
      setUser(null);
      // ★ Гарантированный редирект на /login
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
  };

  // ─── Автовыход через час бездействия ───
  // Стабильный callback — не меняется между рендерами
  const handleIdleLogout = useCallback(() => {
    console.log('[idle] handleIdleLogout called');
    logout().catch(e => console.error('[idle] logout error:', e));
  }, []);

  const idleLogout = useIdleLogout({
    onLogout: handleIdleLogout,
    enabled: !!user,
    warningEnabled: true,
  });
  const updateUser = async (updates: Partial<User>) => {
    try {
      const res = await api.put('/auth/profile', updates);
      setUser(res.data.user);
    } catch (err) {
      console.error('Update failed:', err);
      throw err;
    }
  };
  if (loading) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>Загрузка...</div>;
  }
  return (
    <AuthContext.Provider value={{ user, loading, login, verify2FA, register, logout, updateUser, refreshUser }}>
      {children}
      {idleLogout.showWarning && (
        <IdleWarningModal
          secondsLeft={idleLogout.secondsLeft}
          onExtend={idleLogout.extend}
          onLogout={idleLogout.logoutNow}
        />
      )}
    </AuthContext.Provider>
  );
};
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};